import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ExploreServiceState } from '../redux/exploreServiceSlice';
import ExploreServiceLocation, { PlaceLocation } from '../types/ExploreServiceLocation';
import { ExploreServiceRecordType } from '../types/ExploreServicePayload';
import getAdminExploreServiceFilters from './getAdminExploreServiceFilters';
import useTrailEngagementRecordIds from './useTrailEngagementRecordIds';

type UseGeneralExploreServiceQueryArgsParams = {
  hasTrails?: boolean;
  isPhotospotOverlaySelected?: boolean;
  recordTypesToReturn?: ExploreServiceRecordType[];
  shouldFetchParks?: boolean;
};

const useGeneralExploreServiceQueryArgs = ({
  hasTrails = false,
  isPhotospotOverlaySelected = false,
  recordTypesToReturn,
  shouldFetchParks = false
}: UseGeneralExploreServiceQueryArgsParams) => {
  const { adminFilters, boundingBox, distanceAwayIsochrone, filters, locationResult, mapRotation, naturalLanguageQuery, sort, trailEngagement } =
    useSelector((state: { exploreService: ExploreServiceState }) => state.exploreService);

  const recordIds = useTrailEngagementRecordIds(trailEngagement);
  const filtersWithRecordIds = useMemo(() => {
    if (recordIds) {
      return { ...filters, hasTrails, recordIds };
    } else {
      return { ...filters, hasTrails };
    }
  }, [filters, hasTrails, recordIds]);

  const filtersWithAdminFilters = useMemo(() => {
    const adminSpecificFilters = getAdminExploreServiceFilters(adminFilters);

    if (Object.keys(adminSpecificFilters).length > 0) {
      return { ...filtersWithRecordIds, ...adminSpecificFilters };
    } else {
      return filtersWithRecordIds;
    }
  }, [adminFilters, filtersWithRecordIds]);

  const queryArgs = useMemo(() => {
    const polygon = distanceAwayIsochrone
      ? distanceAwayIsochrone.coordinates[0].map(position => {
          return { lat: position[1], lng: position[0] };
        })
      : undefined;
    const placeLocation: PlaceLocation | undefined =
      locationResult && 'location_type' in locationResult && locationResult.location_type
        ? { id: locationResult.ID, placeType: locationResult.location_type }
        : undefined;

    const recordTypes: ExploreServiceRecordType[] = [
      ...(recordTypesToReturn || (['trail', 'poi'] as ExploreServiceRecordType[])),
      ...(isPhotospotOverlaySelected ? (['photospot'] as ExploreServiceRecordType[]) : []),
      ...(shouldFetchParks ? (['area'] as ExploreServiceRecordType[]) : [])
    ];

    return {
      filters: filtersWithAdminFilters,
      location: {
        mapRotation,
        ...(boundingBox ?? { bottomLeft: undefined, bottomRight: undefined, topLeft: undefined, topRight: undefined }),
        // Don't send the locationResult when a we have a polygon since the polygon should take priority
        ...(!polygon ? placeLocation : {}),
        ...(polygon ? { polygons: [polygon] } : {})
      } as ExploreServiceLocation,
      query: naturalLanguageQuery,
      recordTypeLimits: shouldFetchParks ? { area: 20 } : undefined,
      recordTypesToReturn: recordTypes,
      sort
    };
  }, [
    boundingBox,
    distanceAwayIsochrone,
    filtersWithAdminFilters,
    isPhotospotOverlaySelected,
    locationResult,
    mapRotation,
    naturalLanguageQuery,
    recordTypesToReturn,
    shouldFetchParks,
    sort
  ]);

  return queryArgs;
};

export default useGeneralExploreServiceQueryArgs;
